import { xmlToJson } from "@utils/xml";
import {
    ParsedDiscount,
    ParsedEDocument,
    ParsedExoneracion,
    ParsedLine,
    ParsedMedioPago,
    ParsedOtherCharge,
    ParsedParty,
    ParsedReference,
    ParsedSummary,
    ParsedTax,
} from "./types";

/**
 * Opciones propias, distintas de `defaultXmlOptions` de `@utils/xml`.
 *
 * El parser por defecto convierte los tags a numero, y eso destruye los codigos fiscales:
 * `CodigoTarifaIVA` "08" quedaria en 8, `CodigoCABYS` perderia los ceros a la izquierda y
 * `UnidadMedida` "1" dejaria de ser texto. Aqui todo llega como string y la conversion a
 * numero se hace explicitamente donde corresponde.
 */
const IMPORT_XML_OPTIONS = {
    ignoreAttributes: true,
    parseTagValue: false,
    parseAttributeValue: false,
    trimValues: true,
    // Los comprobantes declaran el namespace de Hacienda; sin esto la raiz llegaria
    // prefijada y ninguna definicion la reconoceria.
    removeNSPrefix: true,
} as const;

/** Raiz de las respuestas de Hacienda, que viajan dentro de los mismos zips. */
export const HACIENDA_RESPONSE_ROOT = "MensajeHacienda";

export class EDocumentParseError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "EDocumentParseError";
    }
}

const text = (raw: unknown): string => {
    if (raw === undefined || raw === null) return "";
    if (typeof raw === "object") return "";
    return `${raw}`.trim();
};

const num = (raw: unknown): number => {
    const value = Number(text(raw));
    return Number.isFinite(value) ? value : 0;
};

/** Un tag que puede repetirse llega como objeto cuando aparece una sola vez. */
const asArray = <T>(raw: unknown): T[] => {
    if (raw === undefined || raw === null) return [];
    return Array.isArray(raw) ? (raw as T[]) : [raw as T];
};

const record = (raw: unknown): Record<string, unknown> =>
    raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};

/** `Ubicacion` aplanada a una linea legible; el modelo guarda la direccion como texto. */
const flattenUbicacion = (raw: unknown): string => {
    const ubicacion = record(raw);
    return [text(ubicacion.Barrio), text(ubicacion.OtrasSenas)].filter(Boolean).join(", ");
};

const parseParty = (raw: unknown): ParsedParty => {
    const party = record(raw);
    const identificacion = record(party.Identificacion);
    const telefono = record(party.Telefono);

    return {
        nombre: text(party.Nombre),
        nombreComercial: text(party.NombreComercial),
        identificacion: {
            tipo: text(identificacion.Tipo),
            numero: text(identificacion.Numero),
        },
        correo: text(party.CorreoElectronico),
        telefonoCodigoPais: text(telefono.CodigoPais),
        telefono: text(telefono.NumTelefono),
        direccion: flattenUbicacion(party.Ubicacion),
    };
};

const parseExoneracion = (raw: unknown): ParsedExoneracion | undefined => {
    const exoneracion = record(raw);
    if (!Object.keys(exoneracion).length) return undefined;

    return {
        // v4.4 renombro el tipo a `TipoDocumentoEX1`; los comprobantes 4.3 usan `TipoDocumento`.
        tipoDocumento: text(exoneracion.TipoDocumentoEX1) || text(exoneracion.TipoDocumento),
        tipoDocumentoOtro: text(exoneracion.TipoDocumentoOTRO),
        numeroDocumento: text(exoneracion.NumeroDocumento),
        articulo: text(exoneracion.Articulo),
        inciso: text(exoneracion.Inciso),
        nombreInstitucion: text(exoneracion.NombreInstitucion),
        nombreInstitucionOtros: text(exoneracion.NombreInstitucionOtros),
        fechaEmision: text(exoneracion.FechaEmisionEX) || text(exoneracion.FechaEmision),
        // `TarifaExonerada` en v4.4, `PorcentajeExoneracion` en v4.3.
        tarifaExonerada: num(exoneracion.TarifaExonerada ?? exoneracion.PorcentajeExoneracion),
        montoExoneracion: num(exoneracion.MontoExoneracion),
    };
};

const parseTaxes = (raw: unknown): ParsedTax[] =>
    asArray<Record<string, unknown>>(raw).map((impuesto) => ({
        codigo: text(impuesto.Codigo) || text(impuesto.CodigoImpuestoOTRO),
        codigoTarifaIVA: text(impuesto.CodigoTarifaIVA),
        tarifa: num(impuesto.Tarifa),
        monto: num(impuesto.Monto),
        exoneracion: parseExoneracion(impuesto.Exoneracion),
    }));

const parseDiscounts = (raw: unknown): ParsedDiscount[] =>
    asArray<Record<string, unknown>>(raw).map((descuento) => ({
        monto: num(descuento.MontoDescuento),
        codigo: text(descuento.CodigoDescuento) || text(descuento.CodigoDescuentoOTRO),
        naturaleza: text(descuento.NaturalezaDescuento),
    }));

/**
 * El codigo del vendedor. v4.4 lo pone en `CodigoComercial` (hasta cinco), mientras que los
 * comprobantes viejos lo traen en `Codigo`. Se toma el primero disponible porque es la llave
 * con la que la carga liga la linea contra el inventario.
 */
const parseLineCode = (line: Record<string, unknown>): string => {
    const comerciales = asArray<Record<string, unknown>>(line.CodigoComercial);
    for (const comercial of comerciales) {
        const codigo = text(comercial.Codigo);
        if (codigo) return codigo;
    }

    const legacy = line.Codigo;
    if (legacy && typeof legacy === "object") {
        return text(record(legacy).Codigo);
    }
    return text(legacy);
};

const parseLine = (raw: Record<string, unknown>, index: number): ParsedLine => ({
    numeroLinea: num(raw.NumeroLinea) || index + 1,
    codigo: parseLineCode(raw),
    codigoCabys: text(raw.CodigoCABYS),
    cantidad: num(raw.Cantidad),
    unidadMedida: text(raw.UnidadMedida),
    detalle: text(raw.Detalle),
    precioUnitario: num(raw.PrecioUnitario),
    montoTotal: num(raw.MontoTotal),
    descuentos: parseDiscounts(raw.Descuento),
    subTotal: num(raw.SubTotal),
    impuestos: parseTaxes(raw.Impuesto),
    impuestoNeto: num(raw.ImpuestoNeto),
    montoTotalLinea: num(raw.MontoTotalLinea),
    partidaArancelaria: text(raw.PartidaArancelaria),
});

const parseSummary = (raw: unknown): ParsedSummary => {
    const resumen = record(raw);
    const moneda = record(resumen.CodigoTipoMoneda);

    return {
        codigoMoneda: text(moneda.CodigoMoneda) || "CRC",
        tipoCambio: num(moneda.TipoCambio) || 1,
        totalVenta: num(resumen.TotalVenta),
        totalDescuentos: num(resumen.TotalDescuentos),
        totalImpuesto: num(resumen.TotalImpuesto),
        totalComprobante: num(resumen.TotalComprobante),
        totalIVADevuelto: num(resumen.TotalIVADevuelto),
        totalOtrosCargos: num(resumen.TotalOtrosCargos),
    };
};

const parseMediosPago = (raw: unknown): ParsedMedioPago[] =>
    asArray<unknown>(raw).map((entry) => {
        // v4.3 ponia el codigo directo en el tag; v4.4 lo anida bajo `TipoMedioPago`.
        if (!entry || typeof entry !== "object") {
            return { codigo: text(entry), otros: "" };
        }
        const medio = record(entry);
        const total = text(medio.TotalMedioPago);
        return {
            codigo: text(medio.TipoMedioPago),
            otros: text(medio.MedioPagoOtros),
            total: total ? num(total) : undefined,
        };
    });

/**
 * `IdentificacionTercero` es un `IdentificacionType` en v4.4, asi que `text()` sobre el nodo
 * devuelve "" y se perderia la identificacion del tercero. Un comprobante viejo puede traer
 * solo el numero en el tag; en ese caso el tipo queda vacio.
 */
const parseThirdPartyId = (raw: unknown): { tipo: string; numero: string } => {
    if (raw && typeof raw === "object") {
        const node = raw as Record<string, unknown>;
        return { tipo: text(node.Tipo), numero: text(node.Numero) };
    }
    return { tipo: "", numero: text(raw) };
};

const parseOtherCharges = (raw: unknown): ParsedOtherCharge[] =>
    asArray<Record<string, unknown>>(raw).map((cargo) => ({
        tipoDocumento: text(cargo.TipoDocumentoOC),
        tipoDocumentoOtros: text(cargo.TipoDocumentoOTROS),
        identificacionTercero: parseThirdPartyId(cargo.IdentificacionTercero),
        nombreTercero: text(cargo.NombreTercero),
        detalle: text(cargo.Detalle),
        porcentaje: num(cargo.PorcentajeOC ?? cargo.Porcentaje),
        montoCargo: num(cargo.MontoCargo),
    }));

const parseReferences = (raw: unknown): ParsedReference[] =>
    asArray<Record<string, unknown>>(raw).map((referencia) => ({
        tipoDoc: text(referencia.TipoDocIR) || text(referencia.TipoDoc),
        numero: text(referencia.Numero),
        fechaEmision: text(referencia.FechaEmisionIR) || text(referencia.FechaEmision),
        codigo: text(referencia.Codigo) || text(referencia.CodigoReferenciaOTRO),
        razon: text(referencia.Razon),
    }));

const readRoots = (xml: string): { rootName: string; root: Record<string, unknown> } => {
    const parsed = xmlToJson<Record<string, unknown>>(xml, IMPORT_XML_OPTIONS);
    // fast-xml-parser deja `?xml` como clave cuando el documento trae declaracion.
    const rootName = Object.keys(parsed).filter((key) => !key.startsWith("?"))[0] || "";
    return { rootName, root: record(parsed[rootName]) };
};

/** Nombre de la raiz del XML, que es lo que identifica el tipo de comprobante. */
export const readRootName = (xml: string): string => readRoots(xml).rootName;

/**
 * Parsea cualquier comprobante electronico v4.4 a una forma comun.
 *
 * Deliberadamente no filtra por tipo: devuelve `rootName` y deja que la definicion del
 * documento decida si lo acepta. Asi el mismo parser sirve para factura, tiquete y, cuando
 * se implementen, notas de credito y debito.
 */
export const parseEDocumentXml = (xml: string): ParsedEDocument => {
    const { rootName, root } = readRoots(xml);

    if (!rootName) {
        throw new EDocumentParseError("El archivo no es un XML de comprobante electronico valido");
    }

    const warnings: string[] = [];
    const detalle = record(root.DetalleServicio);
    const rawLines = asArray<Record<string, unknown>>(detalle.LineaDetalle);

    // `LineaDetalleSurtido` cuelga de cada linea, no del detalle: hay que buscarlo por linea.
    if (rawLines.some((line) => Object.keys(record(line.DetalleSurtido)).length > 0)) {
        warnings.push("El comprobante usa lineas de surtido, que no se importan");
    }

    const resumen = record(root.ResumenFactura);

    return {
        rootName,
        clave: text(root.Clave),
        numeroConsecutivo: text(root.NumeroConsecutivo),
        fechaEmision: text(root.FechaEmision),
        codigoActividadEmisor: text(root.CodigoActividadEmisor) || text(root.CodigoActividad),
        codigoActividadReceptor: text(root.CodigoActividadReceptor),
        emisor: parseParty(root.Emisor),
        receptor: root.Receptor ? parseParty(root.Receptor) : undefined,
        condicionVenta: text(root.CondicionVenta),
        condicionVentaOtros: text(root.CondicionVentaOtros),
        plazoCredito: text(root.PlazoCredito),
        // v4.4 movio `MedioPago` dentro de `ResumenFactura`; en v4.3 colgaba de la raiz.
        mediosPago: parseMediosPago(resumen.MedioPago ?? root.MedioPago),
        resumen: parseSummary(resumen),
        otrosCargos: parseOtherCharges(root.OtrosCargos),
        referencias: parseReferences(root.InformacionReferencia),
        lineas: rawLines.map(parseLine),
        warnings,
    };
};
